import React, { useEffect, useRef, useState, useCallback } from "react";
import { BellRing, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

export const NotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openItem = async (n) => {
    if (!n.read) { try { await api.put(`/notifications/${n.id}/read`); } catch { /* silent */ } }
    setOpen(false);
    load();
    if (n.link) nav(n.link);
  };

  const readAll = async () => {
    try { await api.put("/notifications/read-all"); load(); } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-stone-100 transition relative" data-testid="notifications-btn">
        <BellRing className="w-4 h-4 text-[#0F2A4A]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center" style={{ background: "#72BE44" }} data-testid="notifications-unread-count">{unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-stone-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notifications-dropdown">
          <div className="px-4 py-2.5 border-b border-stone-100 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0F2A4A]">Notifications</span>
            {unread > 0 && (
              <button onClick={readAll} data-testid="notifications-read-all" className="text-[10px] text-[#72BE44] font-medium flex items-center gap-1 hover:underline">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && <div className="px-4 py-8 text-center text-xs text-stone-400">No notifications yet</div>}
            {items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-stone-50 hover:bg-stone-50 transition ${n.read ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: "#72BE44" }} />}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-stone-900 truncate">{n.title}</div>
                    {n.body && <div className="text-[11px] text-stone-500 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-stone-400 mt-0.5">{new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
